import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  BarElement,
  CategoryScale,
  Chart,
  ChartData,
  ChartOptions,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js';
import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';

Chart.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface AdminAnalyticsBarChartProps {
  labels: string[];
  values: number[];
  title: string;
  xLabel?: string;
  yLabel?: string;
  height?: number;
  color?: string;
  horizontal?: boolean;
}

const AdminAnalyticsBarChart: React.FC<AdminAnalyticsBarChartProps> = ({
  labels,
  values,
  title,
  xLabel,
  yLabel,
  height = 400,
  color,
  horizontal = false,
}) => {
  const theme = useTheme();
  const barColor = color || theme.palette.primary.main;
  const textColor = theme.palette.text.secondary;
  const gridColor = theme.palette.divider;

  const chartData: ChartData<'bar'> = useMemo(
    () => ({
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: barColor,
          borderRadius: 3,
          maxBarThickness: 60,
        },
      ],
    }),
    [labels, values, barColor]
  );

  const options: ChartOptions<'bar'> = useMemo(
    () => ({
      indexAxis: horizontal ? ('y' as const) : ('x' as const),
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: { display: true, text: title, color: textColor, font: { size: 14 } },
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.parsed[horizontal ? 'x' : 'y'].toLocaleString()}`,
          },
        },
      },
      scales: {
        x: {
          title: xLabel ? { display: true, text: xLabel, color: textColor } : undefined,
          ticks: { color: textColor, maxRotation: horizontal ? 0 : 45 },
          grid: { color: gridColor },
        },
        y: {
          title: yLabel ? { display: true, text: yLabel, color: textColor } : undefined,
          ticks: { color: textColor },
          grid: { color: gridColor },
        },
      },
    }),
    [title, textColor, gridColor, xLabel, yLabel, horizontal]
  );

  if (labels.length === 0) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.secondary">No data</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height, position: 'relative' }}>
      <Bar data={chartData} options={options} />
    </Box>
  );
};

export default AdminAnalyticsBarChart;
